import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';

import get from 'lodash/get';
import defaultTo from 'lodash/defaultTo';
import some from 'lodash/some';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { fromGlobalId } from 'graphql-relay';
import Container from '../../components/Container';
import { formatLocationByPath } from '../../utils/location-utils';

import Icon from '../../components/Icon';
import Table from '../../components/Table';
import Button from '../../components/Button';
import Divider from '../../components/Divider';
import Item from '../../components/Item';
import Loader from '../../components/Loader';
import Modal from '../../components/Modal';
import FeatureEnabled from '../../components/FeatureEnabled';
import SubscriptionActive from '../../components/SubscriptionActive';
import HighSchoolDetailDisplay from '../HighSchoolDetailsDisplay';
import { REPVISITS_LOOKING_FOR_YOU_COUNT, REP_VISITS_SUBSCRIPTION } from '../Authorization/constants';

import './styles.css';

const messages = defineMessages({
  noResults: {
    id: 'search-results.noneFound',
    defaultMessage: 'No results found.',
  },
  tryAgain: {
    id: 'search-results.tryAgain',
    defaultMessage: 'Please try a new search.',
  },
  moreResults: {
    id: 'search-results.moreResults',
    defaultMessage: 'More Results',
  },
  type: {
    id: 'repvisits.travel.plan.type',
    defaultMessage: 'Type',
  },
  collegeGoingRate: {
    id: 'repvisits.travel.plan.collegeGoingRate',
    defaultMessage: 'College Going Rate',
  },
  seniorClassSize: {
    id: 'repvisits.travel.plan.seniorClassSize',
    defaultMessage: 'Senior Class Size',
  },
  interestedStudents: {
    id: 'repvisits.travel.plan.interestedStudents',
    defaultMessage: 'Interested Students',
  },
  isntUsingRepVisits: {
    id: 'repvisits.travel.plan.isntUsingRepVisits',
    defaultMessage: 'This school is not using RepVisits yet',
  },
  viewAvailability: {
    id: 'repvisits.travel.plan.viewAvailability',
    defaultMessage: 'View Availability',
  },
  addToTravelPlan: {
    id: 'repvisits.travel.plan.addHighSchool',
    defaultMessage: 'Add To Travel Plan',
  },
  addedToTravelPlan: {
    id: 'repvisits.travel.plan.addedHighSchool',
    defaultMessage: 'Added To Travel Plan',
  },
});

class RecommendationsSearchResults extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.object,
    availabilityCallback: PropTypes.func,
    travelPlanCallback: PropTypes.func,
    loadMore: PropTypes.func,
    addTravelPlan: PropTypes.func.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      loading: false,
    };
  }

  resolveRecommendationScore = (school) => (defaultTo(get(school, 'recommendationScore'), 'NA'));

  addTravelPlan = (schoolId) => {
    const onFailure = () => {
      /* istanbul ignore next */
      this.props.travelPlanCallback(false);
    };

    const onSuccess = () => {
      /* istanbul ignore next */
      this.props.travelPlanCallback(true);
    };

    const input = { highSchoolId: schoolId };

    this.props.addTravelPlan({ variables: { input } })
      .then(() => onSuccess())
      .catch(() => onFailure());
  };

  loadMore = () => {
    this.setState({ loading: true });
    this.props.loadMore('viewer.recommendedHighSchoolSearchByLocation').finally(() => this.setState({ loading: false }));
  };

  render() {
    const { viewer, availabilityCallback } = this.props;
    if (!get(viewer, 'recommendedHighSchoolSearchByLocation.edges.length')) {
      return (
        <Item.Group>
          <Item styleName="noResultsContainer">
            <Item.Content>
              <Item.Header styleName="noResultsHeader"><FormattedMessage {...messages.noResults} /></Item.Header>
              <Item.Description styleName="noResultsDescription">
                <FormattedMessage {...messages.tryAgain} />
              </Item.Description>
            </Item.Content>
          </Item>
        </Item.Group>
      );
    }
    return (
      <div>
        <Table basic="very" data-cy="search-results-table">
          <Table.Body>
            {viewer.recommendedHighSchoolSearchByLocation.edges.map((edge) => {
              const school = edge.node;
              const { repVisitsTravelPlan } = viewer;
              const recommendationScore = this.resolveRecommendationScore(school);
              const visitsVisible = get(school, 'repVisitsAccount.availabilityVisible');
              const existingTravelPlan = some(repVisitsTravelPlan, ['id', school.id]);

              return (
                <Table.Row styleName="result-row" key={school.id}>
                  <Table.Cell styleName="rating-col">
                    <div styleName="numberCircle">
                      <span styleName="CircleCenter" data-cy="search-results-CircleCenter">
                        {recommendationScore}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell width={4}>
                    <Modal
                      basic
                      styleName="details-modal"
                      trigger={<Link styleName="hsLink" to="">{school.name}</Link> /* eslint-disable-line jsx-a11y/anchor-is-valid */}
                      closeIcon={<Icon name="close" styleName="details-modal-close" />}
                    >
                      <HighSchoolDetailDisplay
                        contactType="visits"
                        id={fromGlobalId(school.id).id}
                      />
                    </Modal>
                    <div styleName="info-text-top">
                      {`${school.address.field_hs_el_county_name ? `${school.address.field_hs_el_county_name}, ` : ''}
                       ${formatLocationByPath(school, 'address.field_hs_elna_city', 'address.field_hs_elna_state_latest')}`}
                    </div>
                  </Table.Cell>
                  <Table.Cell width={1}>
                    <div styleName="info-text-top">
                      {school.general.field_hs_elna_school_type}
                    </div>
                    <span styleName="info-text-top">
                      <FormattedMessage {...messages.type} />
                    </span>
                  </Table.Cell>
                  <Table.Cell width={3}>
                    <div styleName="info-text-top">
                      {`${school.naviance.field_hs_na_percent_college}%`}
                    </div>
                    <span styleName="info-text-top">
                      <FormattedMessage {...messages.collegeGoingRate} />
                    </span>
                  </Table.Cell>
                  <Table.Cell width={3}>
                    <div styleName="info-text-top">
                      {school.enrollmentByGrade.field_hs_el_grade_12_students}
                    </div>
                    <span styleName="info-text-top">
                      <FormattedMessage {...messages.seniorClassSize} />
                    </span>
                  </Table.Cell>
                  <FeatureEnabled allOf={REPVISITS_LOOKING_FOR_YOU_COUNT}>
                    <SubscriptionActive anyOf={[REP_VISITS_SUBSCRIPTION]}>
                      <Table.Cell width={2}>
                        <div styleName="info-text-top">
                          {school.interestedStudents < 10 ? ('N/A') : school.interestedStudents }
                        </div>
                        <span styleName="info-text-top">
                          <FormattedMessage {...messages.interestedStudents} />
                        </span>
                      </Table.Cell>
                    </SubscriptionActive>
                  </FeatureEnabled>
                  <Table.Cell textAlign="center" width={3}>
                    <div>
                      {visitsVisible ? (
                        <Button styleName="view-button" onClick={() => availabilityCallback(fromGlobalId(school.id).id)}>
                          <FormattedMessage {...messages.viewAvailability} />
                        </Button>
                      ):
                        <FormattedMessage {...messages.isntUsingRepVisits} styleName="info-text-top" />}
                    </div>
                    {existingTravelPlan ? (
                      <div styleName="info-text-top">
                        <FormattedMessage {...messages.addedToTravelPlan} />
                      </div>
                    ): (
                      <div styleName="info-text-top">
                        {visitsVisible && <div> or{' '} </div>}
                        <Button basic onClick={() => this.addTravelPlan(school.id)} name="Add to travel plan">
                          <FormattedMessage {...messages.addToTravelPlan} />
                        </Button>
                      </div>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
        { get(viewer, 'recommendedHighSchoolSearchByLocation.pageInfo.hasNextPage') ? (
          <Container textAlign="center" styleName="viewButton">
            <Divider />
            <Loader inline active={this.state.loading} />
            <Button size="small" primary onClick={() => this.loadMore()}>
              <FormattedMessage {...messages.moreResults} />
            </Button>
          </Container>
        ): null}
      </div>
    );
  }
}

export default injectIntl(RecommendationsSearchResults);
